import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type MarketSettingsCurrencyConversionProfileController from 'lumen/controllers/market/settings/currency-conversion/profile';
import type {
  Currency,
  CurrencyConversionExchangeRatesProfile,
} from 'lumen/models';
import type CurrencyConversionOrganizationContext from 'lumen/models/currency-conversion/organization-context';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { isErrorWithStatusCode } from 'lumen/utils/budget-forecast/data-fetching';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

const PROFILE_LOCAL_STORAGE_KEY_PREFIX = 'selectedProfileId';

type UnsavedChangesAlertData = {
  confirmButtonText: string;
  text: string;
  title: string;
};

export default class CurrencyConversionService extends Service {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  getBaseCurrenciesTask = task({ restartable: true }, async () => {
    let typeKey = '';
    if (this.isHotelLevel) {
      const spiderMarketId = this.hotelAndMarketService.hotel?.spiderMarketId;

      if (!spiderMarketId) {
        // Portfolio currencies require a market context. Without spiderMarketId,
        // we cannot determine which portfolio currencies apply at hotel level,
        // so return an empty list to suppress the currency-missing badge.
        return [];
      }

      typeKey = `markets/${spiderMarketId}/currencies`;
    } else {
      const marketId = this.hotelAndMarketService.market?.id;
      typeKey = `markets/${marketId}/currencies`;
    }

    try {
      return await this.store.query<Currency>('currency', {
        namespace: APIGATEWAY,
        typeKey,
      });
    } catch (error) {
      void this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  getExchangeRatesProfileByIdTask = task(
    { restartable: true },
    async (id: string): Promise<CurrencyConversionExchangeRatesProfile> => {
      try {
        await this.getBaseCurrenciesTask.perform();

        const profile =
          await this.store.findRecord<CurrencyConversionExchangeRatesProfile>(
            'currency-conversion/exchange-rates-profile',
            id,
          );

        /*
          Automatically create exchange rate records for all portfolio currencies that don't already have them (excluding the base currency).
          This allows and nudges users to set rates for all available portfolio currencies.
          */
        const existingRateCurrencyCodes = new Set(
          profile.rates.map(({ currencyCode }) => currencyCode),
        );

        const portfolioCurrencies =
          this.getBaseCurrenciesTask.last?.value ?? [];

        portfolioCurrencies
          .filter(
            ({ code }) =>
              !existingRateCurrencyCodes.has(code) &&
              code !== profile.baseCurrency.code,
          )
          .forEach((currency) => {
            this.store.createRecord('currency-conversion/exchange-rate', {
              currencyCode: currency.code,
              value: null,
              exchangeRatesProfile: profile,
            });
          });

        return profile;
      } catch (error) {
        // Caller is responsible for handling 404 errors.
        if (isErrorWithStatusCode(error) && error.status === 404) {
          throw error;
        }

        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );
  getExchangeRatesProfilesTask = task(
    { restartable: true },
    async (): Promise<CurrencyConversionExchangeRatesProfile[]> => {
      try {
        await this.getBaseCurrenciesTask.perform();

        return await this.store.query<CurrencyConversionExchangeRatesProfile>(
          'currency-conversion/exchange-rates-profile',
          {
            namespace: APIGATEWAY,
            typeKey: 'finance/exchange-rate-profiles',
          },
        );
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  getOrganizationContextTask = task(
    { restartable: true },
    async (): Promise<CurrencyConversionOrganizationContext | null> => {
      try {
        return await this.store.queryRecord<CurrencyConversionOrganizationContext>(
          'currency-conversion/organization-context',
          { namespace: APIGATEWAY },
        );
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  updateDefaultCurrencyTask = task(
    { drop: true },
    async (
      organizationContext: CurrencyConversionOrganizationContext,
    ): Promise<void> => {
      try {
        await organizationContext.save({ adapterOptions: { usePatch: true } });
        this.toast.success(
          this.intl.t('settings.currency_conversion.default_currency.updated'),
        );
      } catch (error) {
        organizationContext.rollbackAttributes();
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  updateProfileTitleTask = task(
    { restartable: true },
    async (
      title: string,
      exchangeRatesProfile: CurrencyConversionExchangeRatesProfile,
    ): Promise<void> => {
      exchangeRatesProfile.title = title;

      try {
        await exchangeRatesProfile.save();
      } catch (error) {
        exchangeRatesProfile.rollbackAttributes();
        throw error;
      }
    },
  );

  get hasCurrencyConversionAccess(): boolean {
    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.CENTRAL_CURRENCY_CONVERSION,
      ) && this.access.hasCurrencyConversionViewAccess
    );
  }

  get isHotelLevel(): boolean {
    return !!this.hotelAndMarketService.hotel;
  }

  get portfolioCurrencies(): Currency[] {
    return this.getBaseCurrenciesTask.last?.value ?? [];
  }

  private get profileLocalStorageKey(): string | null {
    const id = this.isHotelLevel
      ? this.hotelAndMarketService.hotel?.id
      : this.hotelAndMarketService.market?.id;

    return id ? `${PROFILE_LOCAL_STORAGE_KEY_PREFIX}-${id}` : null;
  }

  get selectedProfileId(): string | null {
    const key = this.profileLocalStorageKey;
    return key ? this.localStorage.getItem(key) : null;
  }

  @action
  async createExchangeRatesProfile(profile: {
    baseCurrency: Currency;
    title: string;
  }): Promise<CurrencyConversionExchangeRatesProfile> {
    const newProfile =
      this.store.createRecord<CurrencyConversionExchangeRatesProfile>(
        'currency-conversion/exchange-rates-profile',
        {
          title: profile.title,
          baseCurrency: profile.baseCurrency,
        },
      );

    await newProfile.save({
      adapterOptions: {
        namespace: APIGATEWAY,
        typeKey: 'exchange-rate-profiles',
      },
    });

    return newProfile;
  }

  @action
  async deleteExchangeRatesProfile(
    profile: CurrencyConversionExchangeRatesProfile,
  ): Promise<void> {
    try {
      const title = profile.title;
      await profile.destroyRecord();

      this.usageAnalytics.track('Exchange Rate Profile Deleted', {
        eventTrigger: 'profile-deleted',
      });

      this.toast.success(
        this.intl.t('settings.currency_conversion.profile_deleted', {
          title,
        }),
        {
          showIcon: true,
        },
      );
    } catch (error) {
      void this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  }

  @action
  handleCurrencySelection(
    currencyCodes: string[],
    exchangeRatesProfile: CurrencyConversionExchangeRatesProfile,
  ): void {
    // Remove rate records for currencies that are no longer selected
    exchangeRatesProfile.rates.forEach((rate) => {
      if (!currencyCodes.includes(rate.currencyCode)) {
        rate.deleteRecord();
      }
    });

    // Re-add previously removed rate records if they are selected again
    exchangeRatesProfile.rates.forEach((rate) => {
      if (currencyCodes.includes(rate.currencyCode) && rate.isDeleted) {
        rate.rollbackAttributes();
      }
    });

    // Create new rate records for currencies that don't have them yet
    currencyCodes.forEach((currencyCode) => {
      const existingRate = exchangeRatesProfile.rates.find(
        (rate) => rate.currencyCode === currencyCode,
      );

      if (!existingRate) {
        this.store.createRecord('currency-conversion/exchange-rate', {
          currencyCode,
          value: null,
          exchangeRatesProfile,
        });
      }
    });
  }

  @action
  async onRouteWillChange(
    routeName: string,
    controller: MarketSettingsCurrencyConversionProfileController,
    transition: Transition,
  ): Promise<void> {
    if (
      !controller.exchangeRatesProfile ||
      controller.exchangeRatesProfile.isDeleted
    ) {
      return;
    }

    const { hasUnsavedChanges } = controller.exchangeRatesProfile;

    const { showMissingPortfolioCurrenciesAlert, showMissingCurrencyAlert } =
      controller;

    // Even after saving, hasChanges may remain true while the updateAnExchangeRatesProfileTask is still running.
    // This happens because the task hasn't completed and the controller state isn't updated yet.
    // Therefore, avoid showing the alert if the task is in progress by checking if the task is running.
    if (
      transition.to?.name !== routeName &&
      (hasUnsavedChanges ||
        showMissingPortfolioCurrenciesAlert ||
        showMissingCurrencyAlert) &&
      !this.updateProfileTitleTask.isRunning
    ) {
      transition.abort();

      let alertData: UnsavedChangesAlertData | null = null;
      if (showMissingPortfolioCurrenciesAlert) {
        alertData = {
          title: this.intl.t(
            'settings.currency_conversion.alert.missing_rates.title',
          ),
          text: this.intl.t(
            'settings.currency_conversion.alert.missing_rates.text',
          ),
          confirmButtonText: this.intl.t('general.continue_anyway'),
        };
      } else if (showMissingCurrencyAlert) {
        alertData = {
          title: this.intl.t(
            'settings.currency_conversion.alert.missing_currency.title',
          ),
          text: this.intl.t(
            'settings.currency_conversion.alert.missing_currency.text',
          ),
          confirmButtonText: this.intl.t('general.continue_anyway'),
        };
      } else {
        alertData = {
          title: this.intl.t('general.unsaved_changes.title'),
          text: this.intl.t('general.unsaved_changes.description'),
          confirmButtonText: this.intl.t('general.discard_changes'),
        };
      }

      const canProceedWithAction = await this.showUnsavedChangesAlert(
        controller.discardUnsavedChanges.bind(controller),
        alertData,
      );

      if (!canProceedWithAction) {
        return;
      }

      transition.retry();
    }
  }

  @action
  async saveExchangeRatesProfile(
    exchangeRatesProfile: CurrencyConversionExchangeRatesProfile,
  ): Promise<void> {
    try {
      await exchangeRatesProfile.save({
        adapterOptions: { includeRates: true },
      });
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  }

  @action
  setSelectedProfileId(value: string | null): void {
    const key = this.profileLocalStorageKey;
    if (!key) {
      return;
    }

    if (value) {
      this.localStorage.setItem(key, value);
    } else {
      this.localStorage.removeItem(key);
    }
  }

  @action
  async showUnsavedChangesAlert(
    discardChanges: () => void,
    alertData: UnsavedChangesAlertData,
  ): Promise<boolean> {
    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      title: alertData.title,
      text: alertData.text,
      dangerMode: true,
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: alertData.confirmButtonText,
        },
      },
    });

    if (!confirmWasClicked) {
      return false;
    }

    discardChanges();

    return true;
  }
}

declare module '@ember/service' {
  interface Registry {
    'currency-conversion': CurrencyConversionService;
  }
}
